import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { changePassword } from "../services/api";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const ChangePassword = () => {
  const { t } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const redirectTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting || redirecting || !currentPassword || !newPassword) return;

    setMsg("");
    setError(false);
    setSubmitting(true);

    try {
      await changePassword(currentPassword, newPassword);

      setMsg(t("change_password.success"));
      setRedirecting(true);

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      localStorage.removeItem("email");

      redirectTimer.current = setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      setError(true);

      const apiError = err.response?.data?.error;

      setMsg(
        typeof apiError === "string"
          ? apiError
          : t("change_password.error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="px-4 py-10">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-6 shadow-md sm:p-8">

        <div>
          <h1 className="mb-6 text-center text-2xl font-bold text-gray-800">
            {t("change_password.title")}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 focus-within:border-[#247BA8] focus-within:ring-1 focus-within:ring-[#247BA8]/20">
              <label
                htmlFor="current-password"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("change_password.old")}
              </label>

              <div className="flex items-center gap-3">

                <input
                  id="current-password"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={submitting || redirecting}
                  className="min-w-0 flex-1 border-0 bg-transparent py-1 text-base text-gray-800 outline-none focus:ring-0"
                />

                <button
                  type="button"
                  onClick={() => setShowCurrent((value) => !value)}
                  aria-label={showCurrent ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  aria-pressed={showCurrent}
                  className="rounded-lg p-1 text-gray-500 transition hover:bg-gray-100 hover:text-[#247BA8]"
                >
                  {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 focus-within:border-[#247BA8] focus-within:ring-1 focus-within:ring-[#247BA8]/20">
              <label
                htmlFor="new-password"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("change_password.new")}
              </label>

              <div className="flex items-center gap-3">

                <input
                  id="new-password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting || redirecting}
                  className="min-w-0 flex-1 border-0 bg-transparent py-1 text-base text-gray-800 outline-none focus:ring-0"
                />

                <button
                  type="button"
                  onClick={() => setShowNew((value) => !value)}
                  aria-label={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  aria-pressed={showNew}
                  className="rounded-lg p-1 text-gray-500 transition hover:bg-gray-100 hover:text-[#247BA8]"
                >
                  {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {msg && (
              <p
                role={error ? "alert" : "status"}
                className={`rounded-xl border px-4 py-3 text-center text-sm font-medium ${
                  error
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-green-200 bg-green-50 text-green-700"
                }`}
              >
                {msg}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || redirecting || !currentPassword || !newPassword}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F97316] px-6 py-2.5 text-base font-bold text-white shadow-md transition hover:bg-[#EA580C] focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && (
                <Loader2 size={18} className="animate-spin" />
              )}

              {t("change_password.button")}
            </button>
          </form>
        </div>
      </div>

      {redirecting && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 px-4">
          <div
            role="status"
            className="w-full max-w-sm rounded-3xl bg-white p-8 text-center shadow-2xl"
          >
            <Loader2
              size={38}
              className="mx-auto mb-4 animate-spin text-[#247BA8]"
            />
            <p className="font-semibold text-[#245580]">
              {t("change_password.redirecting")}
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default ChangePassword;
